import React from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  chakra,
  Box,
} from "@chakra-ui/react";
import { TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";
import {
  useReactTable,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
} from "@tanstack/react-table";

export function DataTable({ columns, data, ...props }) {
  const [sorting, setSorting] = React.useState([
    { id: "date", desc: true }, // Default sort by date descending
  ]);

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
  });

  return (
    <Box overflowX="auto">
      <Table
        variant="simple"
        role="table"
        aria-label={props["aria-label"] || "Data table"}
      >
        <Thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <Tr key={headerGroup.id} role="row">
              {headerGroup.headers.map((header) => {
                const isSortable = header.column.getCanSort();
                const sortDirection = header.column.getIsSorted();

                return (
                  <Th
                    key={header.id}
                    onClick={isSortable ? header.column.getToggleSortingHandler() : undefined}
                    cursor={isSortable ? "pointer" : "default"}
                    role="columnheader"
                    scope="col"
                    aria-sort={
                      sortDirection
                        ? sortDirection === "asc"
                          ? "ascending"
                          : "descending"
                        : "none"
                    }
                    tabIndex={isSortable ? 0 : undefined}
                    onKeyDown={(e) => {
                      if (isSortable && (e.key === "Enter" || e.key === " ")) {
                        e.preventDefault();
                        header.column.getToggleSortingHandler()(e);
                      }
                    }}
                    _hover={isSortable ? { bg: "gray.100" } : {}}
                    _focus={
                      isSortable
                        ? {
                            outline: "2px solid",
                            outlineColor: "blue.500",
                            outlineOffset: "-2px",
                          }
                        : {}
                    }
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}

                    {isSortable && (
                      <chakra.span pl="2" aria-hidden="true">
                        {sortDirection === "asc" ? (
                          <TriangleUpIcon aria-label="sorted ascending" />
                        ) : sortDirection === "desc" ? (
                          <TriangleDownIcon aria-label="sorted descending" />
                        ) : null}
                      </chakra.span>
                    )}
                  </Th>
                );
              })}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {table.getRowModel().rows.map((row) => (
            <Tr key={row.id} role="row">
              {row.getVisibleCells().map((cell) => (
                <Td key={cell.id} role="cell">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}